import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { OfferCloneSection, offerCloneSections } from '../../Types/offerTypes';

interface CopyOfferSectionsDialogProps {
  open: boolean;
  sourceOfferId: number | null;
  sourceOfferName?: string;
  onClose: () => void;
  onConfirm: (sectionsToCopy: OfferCloneSection[]) => Promise<void>;
  isLoading: boolean;
  errorMessage?: string;
}

const sectionLabels: Record<OfferCloneSection, string> = {
  basic: 'Podstawowe',
  additionalInformation: 'Dodatkowe informacje',
  seo: 'SEO',
  configuration: 'Konfiguracja',
  categories: 'Kategorie',
  tags: 'Tagi',
  offerOptions: 'Opcje oferty',
  program: 'Program oferty',
  boardingPlaces: 'Miejsca wsiadania',
  images: 'Zdjęcia',
};

const CopyOfferSectionsDialog = ({
  open,
  sourceOfferId,
  sourceOfferName,
  onClose,
  onConfirm,
  isLoading,
  errorMessage,
}: CopyOfferSectionsDialogProps) => {
  const [selectedSections, setSelectedSections] = useState<OfferCloneSection[]>([]);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!open) {
      setSelectedSections([]);
      setError('');
    }
  }, [open]);

  const isAllSelected = useMemo(
    () => selectedSections.length === offerCloneSections.length,
    [selectedSections]
  );

  const toggleSection = (section: OfferCloneSection) => {
    setError('');
    setSelectedSections((prev) =>
      prev.includes(section)
        ? prev.filter((item) => item !== section)
        : [...prev, section]
    );
  };

  const toggleAll = () => {
    setError('');
    setSelectedSections(isAllSelected ? [] : [...offerCloneSections]);
  };

  const handleConfirm = async () => {
    if (selectedSections.length === 0) {
      setError('Wybierz minimum jedną sekcję do skopiowania.');
      return;
    }

    await onConfirm(selectedSections);
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Kopiuj sekcje oferty</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Oferta źródłowa: {sourceOfferName || '-'} (ID: {sourceOfferId ?? '-'})
        </Typography>

        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={isAllSelected} onChange={toggleAll} />}
            label="Zaznacz wszystko"
          />
        </Box>

        <FormGroup>
          {offerCloneSections.map((section) => (
            <FormControlLabel
              key={section}
              control={
                <Checkbox
                  checked={selectedSections.includes(section)}
                  onChange={() => toggleSection(section)}
                />
              }
              label={sectionLabels[section]}
            />
          ))}
        </FormGroup>

        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Anuluj
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={isLoading}>
          {isLoading ? 'Kopiowanie...' : 'Utwórz kopię'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyOfferSectionsDialog;
